import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";
import { ConfirmDialog, useConfirm } from "./chrome.jsx";

const { Button, Card, Badge, Input, Select, Radio, Switch, EmptyState, FluteDivider } =
  window.CardboardmarketDesignSystem_2a3239;

/**
 * Referral draws, from the operator's side.
 *
 * ENGLISH, like every other admin screen and unlike everything traders see.
 * These words only ever appear in our own back office; running them through
 * `t()` would put operator vocabulary in the translation file beside the
 * sentences real people read.
 *
 * WHAT A CAMPAIGN IS, since the word is overloaded in this codebase: a set of
 * conditions over the permanent referral facts of migration 106. It does not
 * own those facts. Two draws can ask different questions of the same history,
 * which is why they are allowed to overlap — the design said one at a time and
 * the owner's answer was that they overlap.
 */

const REQUIREMENTS = [
  { value: "listing", label: "They post a listing" },
  { value: "want", label: "They post a want" },
  { value: "listing_or_want", label: "They post a listing or a want" },
  { value: "trade", label: "They complete a trade" },
  { value: "signup", label: "Nothing — joining is enough" },
];

const STATE_TONE = { live: "brand", scheduled: "ink", draft: "neutral", ended: "neutral" };

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" in LOCAL time; the API speaks ISO. */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

const BLANK = {
  title: "", prize: "", prizeNote: "", winners: 5,
  startsAt: "", endsAt: "", drawAt: "",
  requires: "listing", withinDays: "", capPerTrader: "", friendEntry: true,
  joiningEarnsEntry: false,
  handoff: { type: "post" },
};


/**
 * The prize picture, uploaded and removable.
 *
 * Optimistic about nothing: the preview is what the SERVER came back with, not
 * a local object URL. A picture that appears instantly and then turns out to
 * have been rejected — wrong type, too large, not really an image — is worse
 * than one that takes a second, because the operator walks away believing it
 * worked.
 */

/**
 * A prize note with room in it.
 *
 * REPORTED: "the prize note I believe is too small and a one-line input and I
 * don't know if it's prepared to add details about the 6 different prizes I am
 * giving away." It is one line because `Input` is, and the design system had no
 * Textarea when this was written (it does since 2026-10-21, and this was left
 * because its type is smaller than Textarea's) — so this is a native one wearing
 * the same clothes: identical label,
 * border, radius and focus ring, read from the same tokens rather than guessed,
 * so it does not become the one control on the screen that looks different.
 *
 * NOT A FIX FOR SIX DIFFERENT PRIZES, and the comment is here so nobody thinks
 * it is. A note is prose shown under the prize; it does not tell the draw who
 * won what, so a winner of the second prize is still told they won the first.
 * That needs prizes modelled properly — see the message to the owner.
 */
function NoteField({ label, hint, value, placeholder, disabled, onChange }) {
  const [focus, setFocus] = React.useState(false);
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
      <span style={{ font: "var(--type-label)", textTransform: "uppercase", letterSpacing: "var(--tracking-label)", color: "var(--text-muted)" }}>
        {label}
      </span>
      <textarea
        rows={5}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onChange={onChange}
        style={{
          font: "var(--type-body)",
          fontSize: "var(--text-sm)",
          color: "var(--text-body)",
          background: disabled ? "var(--kraft-100)" : "var(--white)",
          border: `1px solid ${focus ? "var(--border-strong, var(--ink-900))" : "var(--border-default)"}`,
          borderRadius: "var(--radius-sm)",
          boxShadow: focus ? "var(--focus-ring)" : "none",
          padding: "10px 12px",
          resize: "vertical",
          minHeight: 96,
          width: "100%",
          transition: "var(--transition-control), box-shadow var(--dur-fast) var(--ease-standard)",
        }}
      />
      {hint ? (
        <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{hint}</span>
      ) : null}
    </label>
  );
}

function PrizePicture({ campaign, pending, onPick }) {
  const [url, setUrl] = React.useState(campaign.imageUrl || null);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState(null);
  const input = React.useRef(null);

  /*
   * A local preview, and only while there is nowhere to send the file.
   *
   * Revoked when it is replaced or the form closes: an object URL holds the
   * whole file in memory until it is, and an operator trying four photographs
   * would be holding four.
   */
  const [localUrl, setLocalUrl] = React.useState(null);
  React.useEffect(() => {
    if (!pending) { setLocalUrl(null); return undefined; }
    const made = URL.createObjectURL(pending);
    setLocalUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [pending]);

  const upload = async (file) => {
    if (!file) return;
    setProblem(null);
    /*
     * NO CAMPAIGN YET: hold it. The save uploads it the moment there is an id.
     * Nothing is validated here — `processPhoto` on the server is what decides
     * whether these bytes are really an image, and a second opinion in the
     * browser would be a second answer to disagree with.
     */
    if (!campaign.id) { onPick(file); return; }
    setBusy(true);
    try {
      const r = await api.adminCampaignImage(campaign.id, file);
      setUrl(r.url);
      onPick(null);
    } catch (e) {
      setProblem((e && e.message) || "That picture did not go through.");
    } finally {
      setBusy(false);
      // So picking the SAME file again after a failure still fires a change.
      if (input.current) input.current.value = "";
    }
  };

  /* Asked first: the picture is deleted, and only an upload brings one back. */
  const [confirm, confirmDialog] = useConfirm();
  const remove = async () => {
    if (!(await confirm({
      title: "Take this picture off the campaign?",
      description: "It is deleted. Every language without its own picture shows none until another is uploaded.",
      confirmLabel: "Take it off",
    }))) return;
    setBusy(true); setProblem(null);
    try { await api.adminRemoveCampaignImage(campaign.id); setUrl(null); }
    catch (e) { setProblem((e && e.message) || "That did not come off."); }
    finally { setBusy(false); }
  };

  return (
    <div className="cbm-stack" style={{ gap: "var(--space-3)" }}>
      {confirmDialog}
      {/*
        Named for the job it does now that languages can have their own.
        The owner asked for "a default one if we don't have an image for the
        active language, let's take english as default" — this IS that default,
        and putting the English creative here produces exactly it, without
        making English a privileged locale in a Spanish-first product.
      */}
      <span className="cbm-label">Picture &middot; in English</span>
      {url || localUrl ? (
        <>
          <img src={localUrl || url} alt=""
               style={{ maxWidth: 280, borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)" }} />
          {localUrl ? (
            /* Said out loud, because it is not stored yet and closing this form
               without saving would lose it. */
            <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
              Added when you save.
            </span>
          ) : null}
        </>
      ) : (
        <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          None. A draw reads perfectly well as words, so this is optional.
        </span>
      )}
      {problem ? <span style={{ font: "var(--type-body-sm)", color: "var(--status-danger)" }}>{problem}</span> : null}
      <input ref={input} type="file" accept="image/*" hidden
             onChange={(e) => upload(e.target.files && e.target.files[0])} />
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current && input.current.click()}>
          {busy ? "Working…" : (url || localUrl) ? "Replace it" : "Add a picture"}
        </Button>
        {localUrl ? <Button size="sm" variant="ghost" onClick={() => onPick(null)}>Take it off</Button>
         : url ? <Button size="sm" variant="ghost" disabled={busy} onClick={remove}>Take it off</Button> : null}
      </div>
    </div>
  );
}


/**
 * The same draw, in the other language.
 *
 * WHY IT IS A SEPARATE BLOCK and not a second set of fields beside the first:
 * the campaign's own title and prize are what everybody sees by default, and
 * these override it only for a reader in that language. Presenting them as
 * equals would invite an operator to fill one and wonder why the other
 * changed.
 *
 * EACH LANGUAGE SAVES ON ITS OWN. Finishing the Spanish should not wait for
 * the English, and a partial save of both would leave a draw half translated
 * in a way nobody asked for.
 *
 * Clearing either field removes the translation rather than writing half of
 * one — a row with a title and no prize would override the fallback with a
 * blank, which is worse than having no translation at all. The server does
 * that; this just says so.
 */

/**
 * One language's own creative.
 *
 * Deliberately plainer than the campaign's picture control: there is no
 * held-and-uploaded-later case here, because the copy it attaches to already
 * exists by the time this renders. Removing it does not remove a picture from
 * the draw — it falls back to the default one above.
 */
function LocalePicture({ campaignId, locale, label, url, onChanged }) {
  const [confirm, confirmDialog] = useConfirm();
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState(null);
  const input = React.useRef(null);

  const run = async (fn) => {
    setBusy(true); setProblem(null);
    try { await fn(); await onChanged(); }
    catch (e) { setProblem((e && e.message) || "That did not go through."); }
    finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="cbm-stack" style={{ gap: "var(--space-3)" }}>
      {confirmDialog}
      <span className="cbm-label">Picture in {label}</span>
      {url ? (
        <img src={url} alt=""
             style={{ maxWidth: 240, borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)" }} />
      ) : (
        <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          None — {label} readers see the English picture.
        </span>
      )}
      {problem ? <span style={{ font: "var(--type-body-sm)", color: "var(--status-danger)" }}>{problem}</span> : null}
      <input ref={input} type="file" accept="image/*" hidden
             onChange={(e) => {
               const file = e.target.files && e.target.files[0];
               if (file) void run(() => api.adminCampaignCopyImage(campaignId, locale, file));
             }} />
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => input.current && input.current.click()}>
          {busy ? "Working…" : url ? "Replace it" : "Add one"}
        </Button>
        {url ? (
          <Button size="sm" variant="ghost" disabled={busy}
                  onClick={async () => {
                    if (await confirm({
                      title: `Delete the ${label} picture?`,
                      description: `${label} readers see the English picture instead, until another is uploaded.`,
                      confirmLabel: "Delete it",
                    })) run(() => api.adminRemoveCampaignCopyImage(campaignId, locale));
                  }}>
            Use the default
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/*
 * ONLY SPANISH IS AN ADDITION, because the campaign's own fields ARE the
 * English. The owner said so plainly after typing a headline twice: "we should
 * assume the default is english and only give the option of adding spanish."
 *
 * The previous version offered both and made him fill one of them with what he
 * had already written at the top — which is the same words typed twice to
 * satisfy a distinction only the schema cared about.
 */
const LOCALES = [["es-ES", "Spanish"]];


/**
 * The specific things a winner may choose between.
 *
 * A POOL, NOT A PODIUM. The owner's words: "we offer 6 options to all winners,
 * and every winner can pick any one of the 6 options. Even if multiple winners
 * pick the same prize it doesn't matter." So there is no ranking here and no
 * stock to run out — the order is only the order they are shown in.
 *
 * SPANISH BESIDE EACH NAME rather than in a section of its own, which is the
 * one thing this screen has already been corrected about: a list edited twice
 * in two places is the retyping he objected to. One row, both languages, one
 * save.
 */
function PrizeOptions({ campaignId }) {
  const [rows, setRows] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState(null);
  const [savedAt, setSavedAt] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await api.adminCampaignPrizes(campaignId);
      setRows(r.prizes);
    } catch (e) { setProblem((e && e.message) || "We could not load the prizes."); }
  }, [campaignId]);
  React.useEffect(() => { void load(); }, [load]);

  const set = (i, field, value) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [field]: value } : r)));
  const add = () => setRows((rs) => [...rs, { id: null, name: "", nameEs: "", taken: 0 }]);
  const remove = (i) => setRows((rs) => rs.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true); setProblem(null);
    try {
      await api.adminSaveCampaignPrizes(campaignId, rows.map((r) => ({
        id: r.id || undefined, name: r.name, nameEs: r.nameEs, note: r.note, noteEs: r.noteEs,
      })));
      await load();
      setSavedAt(Date.now());
    } catch (e) { setProblem((e && e.message) || "That did not save."); }
    finally { setBusy(false); }
  };

  if (!rows) return <p style={{ color: "var(--text-muted)" }}>Loading prizes…</p>;

  return (
    <Card padding="var(--space-6)" style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
      <div>
        <span className="cbm-label">Which one they can choose</span>
        <p style={{ margin: "4px 0 0", font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          Everyone wins what you wrote above. These are the specific ones they pick from
          afterwards &mdash; any winner can take any of them, and two taking the same is fine.
          Leave it empty if there is nothing to choose between.
        </p>
      </div>

      {problem ? <span style={{ font: "var(--type-body-sm)", color: "var(--status-danger)" }}>{problem}</span> : null}

      {rows.length === 0 ? (
        <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          No options yet.
        </span>
      ) : null}

      <div className="cbm-stack" style={{ gap: "var(--space-4)" }}>
        {rows.map((r, i) => (
          <div key={r.id || `new-${i}`} data-cbm="campaign-prize"
               style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
            <Input wrapperStyle={{ flex: 1, minWidth: 200 }} label={i === 0 ? "In English" : undefined}
                   placeholder="One Piece booster box"
                   value={r.name || ""} onChange={(e) => set(i, "name", e.target.value)} />
            <Input wrapperStyle={{ flex: 1, minWidth: 200 }} label={i === 0 ? "In Spanish" : undefined}
                   placeholder={r.name ? `same as "${r.name}"` : "Caja de sobres de One Piece"}
                   value={r.nameEs || ""} onChange={(e) => set(i, "nameEs", e.target.value)} />
            <Button size="sm" variant="ghost" onClick={() => remove(i)}>Remove</Button>
            {/* Said before they press save, not after: removing an option a
                winner has already taken forgets their choice. */}
            {r.taken > 0 ? (
              <span style={{ width: "100%", font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--status-caution, var(--text-muted))" }}>
                {r.taken} {r.taken === 1 ? "winner has" : "winners have"} chosen this one.
              </span>
            ) : null}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "center", flexWrap: "wrap" }}>
        <Button size="sm" variant="outline" onClick={add}>Add an option</Button>
        <Button size="sm" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save the list"}</Button>
        {savedAt ? <span style={{ font: "var(--type-meta)", color: "var(--text-muted)" }}>Saved.</span> : null}
      </div>
    </Card>
  );
}

function Translations({ campaignId, base }) {
  const [copies, setCopies] = React.useState(null);
  /*
   * WHAT THE SERVER HOLDS, kept apart from what is being typed.
   *
   * `copies` is the edit state and gains a key the moment somebody types a
   * character — so deriving "written" from it made the badge say WRITTEN and
   * the button say "Update" before anything had been saved. The badge is a
   * claim about the server, so it reads a value that only the server changes.
   */
  const [saved, setSaved] = React.useState({});
  const [busy, setBusy] = React.useState(null);
  const [problem, setProblem] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await api.adminCampaignCopy(campaignId);
      const byLocale = {};
      for (const c of r.copies) byLocale[c.locale] = c;
      setCopies(byLocale);
      setSaved(Object.fromEntries(r.copies.map((c) => [c.locale, true])));
    } catch (e) { setProblem((e && e.message) || "We could not load the translations."); }
  }, [campaignId]);
  React.useEffect(() => { void load(); }, [load]);

  const set = (locale, field, value) => setCopies((c) => ({
    ...c, [locale]: { ...(c[locale] || { title: "", prize: "", prizeNote: "" }), [field]: value },
  }));

  const save = async (locale) => {
    setBusy(locale); setProblem(null);
    try {
      const c = copies[locale] || {};
      await api.adminSaveCampaignCopy(campaignId, locale, {
        title: c.title || "", prize: c.prize || "", prizeNote: c.prizeNote || null,
      });
      await load();
    } catch (e) { setProblem((e && e.message) || "That did not save."); }
    finally { setBusy(null); }
  };

  if (!copies) return <p style={{ color: "var(--text-muted)" }}>Loading translations…</p>;

  return (
    <Card padding="var(--space-6)" style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <div>
        <span className="cbm-label">Also in Spanish</span>
        <p style={{ margin: "4px 0 0", font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          Everything above is what English readers see. Fill in only what differs in
          Spanish &mdash; leave a box empty and Spanish readers get the English one.
        </p>
      </div>
      {problem ? <span style={{ font: "var(--type-body-sm)", color: "var(--status-danger)" }}>{problem}</span> : null}
      {LOCALES.map(([locale, label]) => {
        const c = copies[locale] || {};
        const written = Boolean(saved[locale]);
        return (
          <div
            key={locale}
            /* A stable handle, the same convention as the import and draw
               strips. The base form has fields with these exact labels, so
               without one a test reaches for the wrong Headline — which it
               did, and sent a PUT with an empty title. */
            data-cbm={`campaign-copy-${locale}`}
            className="cbm-stack"
            style={{ gap: "var(--space-4)" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ font: "var(--type-ui)", color: "var(--text-strong)" }}>{label}</span>
              {written ? <Badge tone="verified">written</Badge> : <Badge tone="neutral">not written</Badge>}

            </div>
            {/* Placeholders show what a Spanish reader gets today, so an empty
                box is visibly "the same as English" rather than blank. */}
            <Input label="Headline" placeholder={base.title || "same as English"}
                   value={c.title || ""} onChange={(e) => set(locale, "title", e.target.value)} />
            <Input label="What they win" placeholder={base.prize || "same as English"}
                   value={c.prize || ""} onChange={(e) => set(locale, "prize", e.target.value)} />
            <NoteField label="Prize note" placeholder={base.prizeNote || "same as English"}
                       value={c.prizeNote || ""} onChange={(e) => set(locale, "prizeNote", e.target.value)} />
            <div>
              <Button size="sm" variant="outline" disabled={busy === locale} onClick={() => save(locale)}>
                {busy === locale ? "Saving…" : written ? `Update the ${label}` : `Save the ${label}`}
              </Button>
            </div>
            {/*
              A creative for this language, once the words exist.

              Only then, and the server says the same: a picture row with no
              headline would be a translation that is only a photograph, and
              the reading query takes the whole row or none of it. It is also
              the order an operator works in.
            */}
            {/*
              Always, not once the words are saved. Requiring a headline before
              a picture was the bug: a Spanish creative with identical wording
              is a real thing to want, and it cost a retyped headline.
            */}
            <LocalePicture campaignId={campaignId} locale={locale} label={label}
                           url={c.imageUrl || null} onChanged={load} />
          </div>
        );
      })}
    </Card>
  );
}

function CampaignEditor({ initial, frozen, onDone, onCancel }) {
  const [f, setF] = React.useState(initial);
  const [tried, setTried] = React.useState(false);
  // A picture chosen before there is anything to attach it to.
  const [pending, setPending] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [refusal, setRefusal] = React.useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const words = f.title.trim() ? f.title.trim().split(/\s+/).length : 0;
  const errors = {
    title: !f.title.trim() ? "Give it a name." : words > 7 ? "Keep it under 7 words." : null,
    prize: !f.prize.trim() ? "Say what people win." : null,
    startsAt: !f.startsAt ? "Pick when it opens." : null,
    endsAt: !f.endsAt ? "Pick when it closes."
      : f.startsAt && new Date(f.endsAt) <= new Date(f.startsAt) ? "It has to close after it opens." : null,
    drawAt: !f.drawAt ? "Pick when it is drawn."
      : f.endsAt && new Date(f.drawAt) < new Date(f.endsAt) ? "The draw cannot be before it closes." : null,
  };
  const ok = !Object.values(errors).some(Boolean);
  const err = (k) => (tried ? errors[k] : null);

  const [confirm, confirmDialog] = useConfirm();

  const save = async (status) => {
    setTried(true);
    setRefusal(null);
    if (!ok) return;
    /*
     * PUBLISHING IS ASKED FIRST (the owner, 2026-10-23: "confirmation should
     * be added anywhere we're changing something kind of important"). It puts
     * the draw in front of every trader at once, and from the first entry its
     * rules freeze; a draft is the way to look at it before that.
     */
    if (status === "published" && !(await confirm({
      title: `Publish "${f.title.trim()}" now?`,
      description: "It goes in front of every trader, and once somebody earns an entry its rules cannot change. Save it as a draft to finish it first.",
      confirmLabel: "Publish it",
    }))) return;
    setSaving(true);
    try {
      const body = {
        title: f.title.trim(), prize: f.prize.trim(), prizeNote: f.prizeNote.trim() || null,
        winners: Number(f.winners) || 1,
        lookbackDays: f.lookbackDays === "" ? 0 : Number(f.lookbackDays),
        startsAt: fromLocalInput(f.startsAt), endsAt: fromLocalInput(f.endsAt), drawAt: fromLocalInput(f.drawAt),
        requires: f.requires,
        withinDays: f.withinDays === "" ? null : Number(f.withinDays),
        capPerTrader: f.capPerTrader === "" ? null : Number(f.capPerTrader),
        friendEntry: !!f.friendEntry,
        joiningEarnsEntry: !!f.joiningEarnsEntry,
        handoff: f.handoff,
      };
      if (f.id) {
        /*
         * Only what a frozen campaign will take. Sending a rule it has already
         * judged somebody by comes back 409 — see `editCampaign` — and asking
         * for it anyway would turn every copy edit into a refusal.
         */
        const patch = frozen
          ? {
            title: body.title, prize: body.prize, prizeNote: body.prizeNote, handoff: body.handoff,
            /*
             * And the one rule a frozen draw will still take, in one direction.
             * Switching it ON takes nothing from anybody who already holds an
             * entry, and without it the only way to advertise a running draw is
             * to destroy it and start again. `editCampaign` refuses the other
             * direction; this only avoids asking for it.
             */
            ...(body.joiningEarnsEntry ? { joiningEarnsEntry: true } : {}),
          }
          : body;
        if (status) patch.status = status;
        await api.adminUpdateCampaign(f.id, patch);
      } else {
        const made = await api.adminCreateCampaign(body);
        /*
         * FROM HERE THE CAMPAIGN EXISTS, and the form stops being a new one.
         *
         * Found by watching this fail: the picture upload threw, the whole save
         * threw with it, the editor stayed open still believing it was creating
         * — and pressing save again would have made a SECOND campaign. Adopting
         * the id first means every later attempt edits the one that exists.
         */
        set("id", made.id);

        if (pending) {
          try {
            await api.adminCampaignImage(made.id, pending);
            setPending(null);
          } catch (e) {
            /*
             * The picture is the only thing that failed, and the campaign is
             * real. Saying "that did not save" would be untrue and would invite
             * exactly the retry that duplicates it — so this reports the
             * picture, leaves the form open on the campaign that now exists,
             * and stops short of publishing something the operator has not seen
             * finished.
             */
            setRefusal("The campaign was created, but the picture did not upload. Try adding it again.");
            return;
          }
        }
        // Created as a draft always; publishing is a second, deliberate step.
        if (status === "published") await api.adminUpdateCampaign(made.id, { status: "published" });
      }
      onDone();
    } catch (e) {
      setRefusal(e && e.fields ? `The draw has entries: ${e.fields.join(", ")} cannot change now.` : (e && e.message) || "That did not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cbm-stack" style={{ gap: "var(--space-5)", maxWidth: 720 }}>
      <Button variant="ghost" size="sm" iconLeft="arrow-left" onClick={onCancel} style={{ marginLeft: -10 }}>
        All campaigns
      </Button>

      {frozen ? (
        <Card tone="sunken" padding="var(--space-4)">
          <strong>This draw has entries.</strong> Somebody earned one under these rules, so the dates,
          the condition, the cap and the number of winners are fixed. The wording and the prize can
          still change — a clearer sentence never changes who won.
        </Card>
      ) : null}
      {refusal ? (
        <Card tone="sunken" padding="var(--space-4)" style={{ color: "var(--status-danger)" }}>{refusal}</Card>
      ) : null}

      <Card padding="var(--space-6)" style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
        <span className="cbm-label">Prize &middot; in English</span>
        <Input label="Headline" placeholder="Bring a friend, win a booster box" value={f.title}
               onChange={(e) => set("title", e.target.value)} error={err("title")}
               hint={err("title") ? undefined : `${words} of 7 words · sentence case, no exclamation marks`} />
        <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <Input wrapperStyle={{ flex: 2, minWidth: 200 }} label="What they win" placeholder="Sealed booster box"
                 value={f.prize} onChange={(e) => set("prize", e.target.value)} error={err("prize")} />
          <Input wrapperStyle={{ flex: 1, minWidth: 120 }} label="Winners" inputMode="numeric" disabled={frozen}
                 value={String(f.winners)} onChange={(e) => set("winners", e.target.value.replace(/[^0-9]/g, ""))} />
        </div>
        <NoteField label="Prize note"
                   hint="Shown under the prize on the draw's page. Line breaks are kept, so a list of what is on offer works here."
                   placeholder={"Winner picks the game from what the shop has in stock.\n\n1st — sealed booster box\n2nd — a box of sleeves"}
                   value={f.prizeNote} onChange={(e) => set("prizeNote", e.target.value)} />
        {/*
          The picture, on a campaign that may not exist yet.

          IT USED TO SAY "SAVE IT FIRST", because an upload needs somewhere to
          put the file and a new draft has no id. The owner called that what it
          was: "why do I need to save a campaign first to be able to add a
          picture? That's bad UX." He is right — that was our sequencing
          problem handed to the person filling in the form.

          So a picture chosen before the campaign exists is HELD, shown from a
          local preview, and uploaded the moment the save comes back with an
          id. Everything the operator does is in one pass; the two requests are
          our business.

          Never frozen by entries: a clearer photograph of the same box changes
          nothing about who won, exactly like the copy it sits beside.
        */}
        <PrizePicture campaign={f} pending={pending} onPick={setPending} />
      </Card>

      <Card padding="var(--space-6)" style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
        <span className="cbm-label">Dates</span>
        {/*
          Real date inputs, where the design had free text ("Sun 1 Dec"). Those
          are a RENDERING of an instant, in the reader's own locale — and this
          product has two of them and a brand rule about date order. A campaign
          that stored what it will print could not be asked whether it is open.
        */}
        <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <Input wrapperStyle={{ flex: 1, minWidth: 210 }} type="datetime-local" label="Opens" disabled={frozen}
                 value={f.startsAt} onChange={(e) => set("startsAt", e.target.value)} error={err("startsAt")} />
          <Input wrapperStyle={{ flex: 1, minWidth: 210 }} type="datetime-local" label="Closes" disabled={frozen}
                 value={f.endsAt} onChange={(e) => set("endsAt", e.target.value)} error={err("endsAt")} />
        </div>
        <Input type="datetime-local" label="Drawn" disabled={frozen} value={f.drawAt}
               onChange={(e) => set("drawAt", e.target.value)} error={err("drawAt")}
               hint={err("drawAt") ? undefined : "Winners are messaged after this."} />
      </Card>

      <Card padding="var(--space-6)" style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
        <span className="cbm-label">What earns an entry</span>
        <Select label="A friend you invited…" value={f.requires} disabled={frozen}
                onChange={(v) => set("requires", v)} options={REQUIREMENTS} />
        {/*
          TWO NUMBERS, ALIGNED ON THEIR BOXES RATHER THAN THEIR TOPS.

          Reported from a phone: "Within this many days of joining" wrapped to
          two lines, "Most entries per person" did not, and the two fields then
          started at different heights. `flex-end` puts the BOXES level whatever
          the labels do — which is the fix that survives a longer label, and
          eventually a translation.

          The wider minimum is the other half. Below 240px each these two stack
          instead of squeezing side by side, which is what a phone should get
          anyway: two number fields sharing a 380px row are cramped even when
          they line up. Neither label wraps at the width they get after that.
        */}
        <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <Input wrapperStyle={{ flex: 1, minWidth: 240 }} label="Within this many days of joining" inputMode="numeric"
                 placeholder="no limit" disabled={frozen}
                 value={String(f.withinDays)}
                 onChange={(e) => set("withinDays", e.target.value.replace(/[^0-9]/g, ""))} />
          <Input wrapperStyle={{ flex: 1, minWidth: 240 }} label="Most entries per person" inputMode="numeric"
                 placeholder="no cap" disabled={frozen}
                 value={String(f.capPerTrader)}
                 onChange={(e) => set("capPerTrader", e.target.value.replace(/[^0-9]/g, ""))} />
        </div>
        {/*
          HOW FAR BACK THIS DRAW REACHES.

          Without it the referral system is dead between draws: a friend has to
          join while a campaign is open, so on any day with nothing running
          there is no reason to invite anybody. Three separate sentences in the
          interface had to be deleted for claiming otherwise, which is the
          product asking for a rule it did not have.

          Thirty days is a month of effort. It is NOT "count everything" — that
          was the bug this replaced, where a new draw opened holding the whole
          history and would hand its prize to whoever had been here longest.

          Frozen with the other rules once entries exist: widening it afterwards
          hands entries to people who did not have them while somebody else was
          counting theirs.
        */}
        <Input label="Also count friends who joined this many days before it opened" inputMode="numeric"
               hint="0 counts only friends who join while the draw is open."
               disabled={frozen} value={String(f.lookbackDays)}
               onChange={(e) => set("lookbackDays", e.target.value.replace(/[^0-9]/g, ""))} />
        {/*
          Not offered, and not a missing feature: only new accounts can ever
          count, because a trader can be referred exactly once in their
          existence and that is a UNIQUE constraint rather than a setting.
        */}
        <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          Only new accounts count. Somebody can be referred once, ever — it is not a setting.
        </span>
        <FluteDivider />
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-5)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ font: "var(--type-ui)", color: "var(--text-strong)" }}>The friend gets an entry too</div>
            <div style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
              Both ends of the referral earn one.
            </div>
          </div>
          <Switch checked={!!f.friendEntry} disabled={frozen} onChange={(v) => set("friendEntry", v)} />
        </div>

        <FluteDivider />
        {/*
          THE SWITCH THAT MAKES A DRAW ADVERTISABLE.

          Entries came only from referrals, so somebody arriving from an
          advertisement earned nothing — they have nobody to refer on day one,
          and the ad was selling a draw its own audience could not enter.

          Off by default, because a draw written as a referral mechanic should
          keep being one unless an operator says otherwise. The description
          names the requirement rather than saying "just for joining", since the
          bar is whatever this campaign already asks of a referred friend.
        */}
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-5)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ font: "var(--type-ui)", color: "var(--text-strong)" }}>Joining earns an entry</div>
            <div style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
              Anybody who joins and meets the requirement above gets one, with no invite.
              Turn this on for a draw you are advertising: somebody arriving from an ad
              has nobody to invite yet.
            </div>
          </div>
          {/*
            Reachable on a frozen draw, but only to turn ON — which is the
            direction that costs nobody an entry. Once on, it locks with every
            other rule: switching it off would leave entries awarded under a rule
            the campaign no longer shows.
          */}
          <Switch checked={!!f.joiningEarnsEntry}
                  disabled={frozen && !!initial?.joiningEarnsEntry}
                  data-cbm="joining-earns-entry"
                  onChange={(v) => set("joiningEarnsEntry", v)} />
        </div>
      </Card>

      <Card padding="var(--space-6)" style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <span className="cbm-label">How winners get the prize</span>
        {/*
          TWO NAMED OPTIONS, not one checkbox.

          It was a single "Collected at a shop" tick, so unticking it chose the
          other thing without ever naming it — and the owner asked to "add an
          option for shipping" that had in fact been there, invisibly, as the
          unchecked state. A choice a person cannot see is not a choice they can
          make deliberately.

          The values are unchanged (`shop` / `post`), so every campaign already
          written keeps working and no migration is needed. What changed is that
          both are on screen with their names on them.
        */}
        <Radio
          name="handoff"
          value={f.handoff.type === "shop" ? "shop" : "post"}
          onChange={(v) => set("handoff", { type: v })}
          options={[
            {
              value: "post",
              label: "Posted to the winner",
              /*
                Said the way it actually works, which is by hand. The draw's
                rules used to promise "the address you give us", implying a form
                that does not exist — the owner's own description is "I manually
                collect winner's addresses and ship the prize to them".
              */
              description: "You message them for an address after the draw and send it.",
            },
            {
              value: "shop",
              label: "Collected in person",
              description: "The winner shows a pickup code. One is generated for each winner.",
            },
          ]}
        />
      </Card>

      {/*
        Only once the campaign exists, for the same reason the picture control
        needs an id: there is nothing to attach a translation to yet. Unlike
        the picture, this one cannot be held and replayed — three fields per
        language, saved independently, is a form of its own.
      */}
      {/* Both need the campaign to exist: an option list and a translation
          have nowhere to attach until it does. */}
      {f.id ? <PrizeOptions campaignId={f.id} /> : null}
      {f.id ? <Translations campaignId={f.id} base={f} /> : null}

      {confirmDialog}
      <div className="cbm-actionbar">
        <Button size="lg" disabled={saving} onClick={() => save(f.id ? undefined : "published")}>
          {f.id ? "Save changes" : "Create and publish"}
        </Button>
        {!f.id ? (
          <Button size="lg" variant="ghost" disabled={saving} onClick={() => save(null)}>Save as draft</Button>
        ) : null}
      </div>
    </div>
  );
}


/**
 * Who won, and whether they have it yet.
 *
 * THE SEED IS ON SCREEN, not buried in a log. It is what makes the draw
 * checkable rather than merely asserted — winners are the entries ordered by
 * md5(seed || entry_id) — and an operator asked "how do you know that was
 * fair" needs it in front of them, not in a query somebody has to write.
 */
function Winners({ campaign, onBack }) {
  const { messageTrader } = useApp();
  // How this prize travels decides what "done" is called below.
  const shipping = !(campaign.handoff && campaign.handoff.type === "shop");
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  const load = React.useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      setState({ loading: false, data: await api.adminCampaignWinners(campaign.id), error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e });
    }
  }, [campaign.id]);
  React.useEffect(() => { void load(); }, [load]);

  const mark = async (traderId, claimed) => {
    await api.adminMarkClaimed(campaign.id, traderId, claimed);
    await load();
  };

  return (
    <div className="cbm-stack" style={{ gap: "var(--space-5)", maxWidth: 720 }}>
      <Button variant="ghost" size="sm" iconLeft="arrow-left" onClick={onBack} style={{ marginLeft: -10 }}>
        All campaigns
      </Button>
      <div>
        <h2 style={{ margin: 0 }}>{campaign.title}</h2>
        <p style={{ margin: "4px 0 0", color: "var(--text-muted)" }}>
          {campaign.prize} × {campaign.winners} · {shipping
            ? "posted — ask each winner for an address"
            : "collected in person — each winner has a code"}
        </p>
      </div>

      {state.loading ? <p>Loading…</p>
       : state.error ? (
        <EmptyState icon="alert-triangle" title="We could not load the winners"
                    action={<Button onClick={() => load()}>Try again</Button>} />
       ) : !state.data.drawnAt ? (
        <Card tone="sunken" padding="var(--space-5)">
          <strong>Not drawn yet.</strong> The winners are picked automatically once the draw
          date passes and the campaign has closed. Nothing here needs doing.
        </Card>
       ) : state.data.winners.length === 0 ? (
        <EmptyState icon="users" title="Nobody entered"
                    description="The draw closed with no entries, so there is nobody to pick." />
       ) : (
        <>
          <div className="cbm-stack" style={{ gap: "var(--space-3)" }}>
            {state.data.winners.map((w) => (
              <Card key={w.traderId} padding="var(--space-5)"
                    style={{ display: "flex", gap: "var(--space-4)", alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ font: "var(--type-price)", color: "var(--text-muted)", minWidth: 28 }}>{w.place}</span>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ font: "var(--type-ui)", color: "var(--text-strong)" }}>{w.name}</div>
                  <div style={{ font: "var(--type-meta)", color: "var(--text-muted)" }}>
                    {w.entries} {w.entries === 1 ? "entry" : "entries"}
                    {w.pickupCode ? ` · code ${w.pickupCode}` : ""}
                  </div>
                  {/*
                    WHICH BOX TO HAND OVER. The endpoint always sent it and
                    nothing drew it — the owner, 2026-11-02: "I don't see their
                    picks in the admin section anywhere".
                  */}
                  {w.chose ? (
                    <div data-cbm="winner-choice" style={{ font: "var(--type-meta)", color: "var(--text-strong)" }}>
                      Chose: {w.chose}
                    </div>
                  ) : state.data.offersChoice ? (
                    <div data-cbm="winner-choice" style={{ font: "var(--type-meta)", color: "var(--text-muted)" }}>
                      Hasn't chosen yet
                    </div>
                  ) : null}
                </div>
                {w.claimedAt ? <Badge tone="verified">Collected</Badge> : <Badge tone="caution">Waiting</Badge>}
                {/*
                  THE WAY TO ACTUALLY REACH THEM, which is the whole of the
                  posted handoff: "I manually collect winner's addresses and
                  ship the prize to them." Without this the operator has a list
                  of names and has to go and find each one, which is how a
                  feature ends up being finished with database queries.

                  Also the right control for the collected-in-person case — a
                  code is no use to somebody who does not know they have won.
                */}
                <Button size="sm" variant="ghost" onClick={() => messageTrader(w.traderId, w.name)}>
                  Message
                </Button>
                <Button size="sm" variant={w.claimedAt ? "ghost" : "outline"}
                        onClick={() => mark(w.traderId, !w.claimedAt)}>
                  {w.claimedAt ? "Undo" : shipping ? "Mark sent" : "Mark collected"}
                </Button>
              </Card>
            ))}
          </div>
          <Card tone="sunken" padding="var(--space-5)" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="cbm-label">Draw seed</span>
            <code style={{ wordBreak: "break-all", font: "var(--type-meta)" }}>{state.data.seed}</code>
            <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
              Winners are the entries ordered by md5(seed + entry id), best per person, top {campaign.winners}.
              Anybody with this and the entry ids gets the same names.
            </span>
          </Card>
        </>
      )}
    </div>
  );
}

export function AdminCampaigns() {
  const { go } = useApp();
  const [state, setState] = React.useState({ loading: true, campaigns: [], error: null });
  const [editing, setEditing] = React.useState(null);
  const [showingWinners, setShowingWinners] = React.useState(null);
  /*
   * ENDING A DRAW CANNOT BE UNDONE, so it gets asked about first.
   *
   * The owner's rule, and it is broader than this one button: "any kind of
   * action that is destructive and we can't undo should be behind a
   * confirmation." Ending a campaign stops entries at that instant — somebody
   * mid-invitation loses a chance they were inside the rules of, and putting
   * the status back afterwards does not give them those minutes.
   *
   * The campaign is held in state rather than a boolean so the dialog can NAME
   * it. "End this draw?" over a list of four is a question about which one.
   */
  const [ending, setEnding] = React.useState(null);
  const [endingNow, setEndingNow] = React.useState(false);

  const load = React.useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { campaigns } = await api.adminCampaigns();
      setState({ loading: false, campaigns, error: null });
    } catch (e) {
      setState({ loading: false, campaigns: [], error: e });
    }
  }, []);
  React.useEffect(() => { void load(); }, [load]);

  if (showingWinners) {
    return <Winners campaign={showingWinners} onBack={() => { setShowingWinners(null); void load(); }} />;
  }

  if (editing) {
    return (
      <CampaignEditor
        initial={editing.campaign}
        frozen={editing.frozen}
        onCancel={() => setEditing(null)}
        onDone={() => { setEditing(null); void load(); }}
      />
    );
  }

  if (state.loading) return <p>Loading campaigns…</p>;
  if (state.error) {
    return <EmptyState icon="alert-triangle" title="We could not load the campaigns"
                       action={<Button onClick={() => load()}>Try again</Button>} />;
  }

  const edit = (c) => setEditing({
    frozen: c.entries > 0,
    campaign: {
      id: c.id, title: c.title, prize: c.prize, prizeNote: c.prize_note || "",
      winners: c.winners,
      startsAt: toLocalInput(c.starts_at), endsAt: toLocalInput(c.ends_at), drawAt: toLocalInput(c.draw_at),
      requires: c.requires,
      withinDays: c.within_days == null ? "" : String(c.within_days),
      capPerTrader: c.cap_per_trader == null ? "" : String(c.cap_per_trader),
      friendEntry: c.friend_entry,
      joiningEarnsEntry: c.joining_earns_entry,
      lookbackDays: c.lookback_days == null ? 30 : c.lookback_days,
      handoff: c.handoff || { type: "post" },
      imageUrl: c.image_url || null,
    },
  });

  return (
    <div className="cbm-stack" style={{ gap: "var(--space-5)" }}>
      {ending ? (
        /* The shared confirmation (chrome.jsx). Not "End now" again on its
           button: a confirmation whose button repeats the trigger word for
           word asks somebody to tell two identical labels apart while
           deciding something they cannot undo. */
        <ConfirmDialog
          title={`End "${ending.title}" now?`}
          description="Entries stop the moment you do this and it cannot be undone. The draw still happens on its draw date."
          cancelLabel="Leave it running" confirmLabel="Stop taking entries" busyLabel="Stopping…" busy={endingNow}
          onCancel={() => setEnding(null)}
          onConfirm={async () => {
            setEndingNow(true);
            try {
              await api.adminUpdateCampaign(ending.id, { status: "ended" });
              setEnding(null);
              await load();
            } catch (e) {
              /* Left open on failure. Closing it would look like it worked. */
            } finally { setEndingNow(false); }
          }}
        >
          <p style={{ margin: 0, color: "var(--text-muted)" }}>
            {ending.entries > 0
              ? `${ending.entries} ${ending.entries === 1 ? "entry has" : "entries have"} been earned so far. They all stay in the draw.`
              : "Nobody has earned an entry yet."}
          </p>
        </ConfirmDialog>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0 }}>Campaigns</h2>
          {/* The design said "one runs at a time". They overlap now — a trader's
              referrals can count for several draws at once. */}
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)" }}>
            Referral draws. Several can run at once, and a referral counts for every one whose rules it meets.
          </p>
        </div>
        <Button iconLeft="plus" onClick={() => setEditing({ frozen: false, campaign: { ...BLANK } })}>
          New campaign
        </Button>
      </div>

      {state.campaigns.length === 0 ? (
        <EmptyState icon="star" title="No campaigns yet"
                    description="Start one and it shows up here."
                    action={<Button onClick={() => setEditing({ frozen: false, campaign: { ...BLANK } })}>New campaign</Button>} />
      ) : (
        <div className="cbm-stack" style={{ gap: "var(--space-3)" }}>
          {state.campaigns.map((c) => (
            <Card key={c.id} padding="var(--space-5)"
                  style={{ display: "flex", gap: "var(--space-4)", alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ font: "var(--type-ui)", color: "var(--text-strong)" }}>{c.title}</div>
                <div style={{ font: "var(--type-meta)", color: "var(--text-muted)" }}>
                  {c.prize} × {c.winners} · {new Date(c.starts_at).toLocaleDateString()} – {new Date(c.ends_at).toLocaleDateString()}
                </div>
              </div>
              <Badge tone={STATE_TONE[c.state] || "neutral"}>{c.state}</Badge>
              <span style={{ font: "var(--type-price)", color: "var(--text-strong)", minWidth: 70, textAlign: "right" }}>
                {c.entries} <span style={{ font: "var(--type-meta)", color: "var(--text-muted)" }}>
                  {c.entries === 1 ? "entry" : "entries"}
                </span>
              </span>
              {/*
                WHERE THE THING YOU JUST MADE ACTUALLY LIVES.

                Reported: "I created one and now I don't know where it is even
                shown." A back office that can publish something and cannot open
                it makes the operator guess at a URL — so this is the public
                page, the one everybody else sees, one tap from the row.

                Not for a draft, which has no public page: `/api/campaigns`
                refuses to mention one, on purpose.
              */}
              {c.status !== 'draft' ? (
                <Button size="sm" variant="ghost" onClick={() => go('campaign', { id: c.id })}>
                  Open page
                </Button>
              ) : null}
              {/* Only once there is something to look at. Before the draw the
                  screen would say "not drawn yet", which the row already
                  implies by not offering this. */}
              {c.drawn_at ? (
                <Button size="sm" variant="ghost" onClick={() => setShowingWinners(c)}>
                  Winners
                </Button>
              ) : null}
              <Button size="sm" variant="outline" onClick={() => edit(c)}>
                {c.entries > 0 ? "View" : "Edit"}
              </Button>
              {c.state === "live" ? (
                <Button size="sm" variant="ghost" onClick={() => setEnding(c)}>End now</Button>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
